import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { API } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge } from '@/components/Shared';
import { DangerConfirm } from '@/components/DangerConfirm';
import { Checkbox } from '@/components/ui/checkbox';
import { money, shortDate, dateTime } from '@/lib/format';
import { ArrowLeft, Printer, Download, DollarSign, Loader2, Ban, RotateCcw, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const METHODS = ['Cash', 'Check', 'Zelle', 'Venmo', 'Bank Transfer', 'Other'];
const COMPANY = { name: 'Lailany Inc', a1: '351 N Walnut Rd Ste 3', a2: 'Turlock, CA 95382', phone: 'Phone: 209-559-4600' };

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [inv, setInv] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: 0, method: 'Cash', note: '' });
  const [downloading, setDownloading] = useState(false);
  const [action, setAction] = useState(null); // 'void' | 'delete' | 'reset'
  const [restoreInv, setRestoreInv] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/invoices/${id}`);
    setInv(data);
    setPay((p) => ({ ...p, amount: Math.max(0, data.total - data.amount_paid) }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const runAction = async (password) => {
    setBusy(true);
    try {
      const body = { password, confirm: 'CONFIRM', restore_inventory: restoreInv };
      if (action === 'void') { await api.post(`/invoices/${id}/void`, body); toast.success('Invoice voided'); }
      else if (action === 'restore') { await api.post(`/invoices/${id}/restore`, body); toast.success('Invoice restored'); }
      else if (action === 'reset') { await api.post(`/invoices/${id}/reset`, body); toast.success('Invoice reset to Unpaid'); }
      else if (action === 'delete') { await api.post(`/invoices/${id}/delete`, body); toast.success('Invoice deleted'); navigate('/invoices'); return; }
      setAction(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const addPayment = async () => {
    try { await api.post(`/invoices/${id}/payments`, { amount: Number(pay.amount), method: pay.method, note: pay.note }); toast.success('Payment recorded'); setPayOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); } finally { setDownloading(false); }
  };

  if (!inv) return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  const balance = Math.max(0, inv.total - inv.amount_paid);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4 no-print">
        <Button variant="outline" size="sm" onClick={() => navigate('/invoices')}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="invoice-print-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
          <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} data-testid="invoice-download-pdf-button">{downloading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} PDF</Button>
          {isAdmin && inv.payment_status !== 'Paid' && <Button size="sm" onClick={() => setPayOpen(true)} data-testid="invoice-record-payment-button"><DollarSign className="h-4 w-4 mr-1" /> Record Payment</Button>}
          {isAdmin && <Button variant="outline" size="sm" onClick={() => { setRestoreInv(false); setAction('reset'); }} data-testid="invoice-reset-button"><RotateCcw className="h-4 w-4 mr-1" /> Reset Status</Button>}
          {isAdmin && !inv.voided && <Button variant="outline" size="sm" onClick={() => { setRestoreInv(true); setAction('void'); }} data-testid="invoice-void-button"><Ban className="h-4 w-4 mr-1" /> Void</Button>}
          {isAdmin && inv.voided && <Button variant="outline" size="sm" onClick={() => { setRestoreInv(false); setAction('restore'); }} data-testid="invoice-restore-button"><Undo2 className="h-4 w-4 mr-1" /> Restore Void</Button>}
          {isAdmin && <Button variant="destructive" size="sm" onClick={() => { setRestoreInv(true); setAction('delete'); }} data-testid="invoice-delete-button"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>}
        </div>
      </div>

      <Card className="print-area mx-auto max-w-[900px] p-6 sm:p-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="font-heading text-xl font-bold text-[hsl(var(--primary))]">{COMPANY.name}</div>
            <div className="text-sm text-muted-foreground mt-1">{COMPANY.a1}<br/>{COMPANY.a2}<br/>{COMPANY.phone}</div>
          </div>
          <div className="sm:text-right">
            <div className="font-heading text-3xl font-bold" style={{ color: '#123A63' }}>INVOICE</div>
            <div className="text-sm mt-2"><span className="text-muted-foreground">Invoice #:</span> <span className="font-medium">{inv.invoice_number}</span></div>
            <div className="text-sm"><span className="text-muted-foreground">Date:</span> {shortDate(inv.invoice_date)}</div>
            <div className="mt-1" data-testid="invoice-status-badge"><StatusBadge kind="payment" value={inv.payment_status} /></div>
            {inv.voided && <div className="mt-1 inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700" data-testid="invoice-voided-badge">VOIDED</div>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 text-sm">
          <div><div className="text-muted-foreground">Bill To</div><div className="font-medium">{inv.customer_name}</div></div>
          <div><div className="text-muted-foreground">Salesperson</div><div className="font-medium">{inv.employee_name || '-'}</div></div>
          <div><div className="text-muted-foreground">Payment Method</div><div className="font-medium">{inv.payment_method || '-'}</div></div>
        </div>

        <div className="mt-6 overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Brand</TableHead><TableHead>SKU</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Unit Price</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {inv.items.map((it, i) => (
                <TableRow key={`${it.sku}-${i}`}><TableCell className="font-medium">{it.product_name}</TableCell><TableCell>{it.brand}</TableCell><TableCell className="font-mono text-xs">{it.sku}</TableCell><TableCell className="text-right tabular-nums">{it.quantity}</TableCell><TableCell className="text-right tabular-nums">{money(it.unit_price)}</TableCell><TableCell className="text-right tabular-nums">{money(it.total_price)}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="flex justify-end mt-6">
          <div className="w-full sm:w-[320px] rounded-lg border bg-[hsl(var(--secondary))] p-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Original Subtotal</span><span className="tabular-nums">{money(inv.subtotal)}</span></div>
            <div className="flex justify-between text-amber-700"><span>Discount ({inv.discount_percent || 0}%)</span><span className="tabular-nums" data-testid="invoice-discount-amount">- {money(inv.discount_amount || 0)}</span></div>
            <div className="flex justify-between font-semibold text-base border-t pt-1.5"><span>Total</span><span className="tabular-nums" data-testid="invoice-total-amount">{money(inv.total)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Amount Paid</span><span className="tabular-nums">{money(inv.amount_paid)}</span></div>
            <div className="flex justify-between font-semibold" style={{ color: '#0E7490' }}><span>Balance Due</span><span className="tabular-nums">{money(balance)}</span></div>
          </div>
        </div>
        {inv.notes && <div className="mt-6 text-sm"><span className="font-medium">Notes:</span> {inv.notes}</div>}
        {isAdmin && <div className="mt-6 text-xs text-muted-foreground border-t pt-3">COGS: {money(inv.cogs)} · Gross Profit: {money(inv.gross_profit)} · Commission ({Math.round((inv.commission_rate || 0) * 100)}%): {money(inv.commission_amount)} <StatusBadge kind="commission" value={inv.commission_status} /></div>}
      </Card>

      {inv.payments && inv.payments.length > 0 && (
        <Card className="max-w-[900px] mx-auto mt-4 p-5 no-print">
          <h3 className="font-heading font-semibold mb-2">Payment History</h3>
          {inv.payments.map((p) => <div key={p.id} className="flex justify-between text-sm py-1 border-b last:border-0"><span>{p.method} · {dateTime(p.created_at)}</span><span className="tabular-nums font-medium">{money(p.amount)}</span></div>)}
        </Card>
      )}

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Record Payment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Amount</Label><Input type="number" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount-input" className="mt-1.5" /></div>
            <div><Label>Method</Label><Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}><SelectTrigger className="mt-1.5" data-testid="payment-method-select"><SelectValue /></SelectTrigger><SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Note</Label><Input value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button onClick={addPayment} data-testid="payment-save-button">Save Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <DangerConfirm
        open={!!action}
        onOpenChange={(o) => !o && setAction(null)}
        title={action === 'void' ? 'Void this invoice?' : action === 'restore' ? 'Restore this invoice?' : action === 'reset' ? 'Reset payment status?' : 'Delete this invoice?'}
        description={action === 'void'
          ? 'Voiding keeps the record but removes it from balances, revenue, profit and commissions.'
          : action === 'restore'
            ? 'This brings the voided invoice back into balances, reports and commissions. If inventory was restored on void, stock will be adjusted back.'
            : action === 'reset'
              ? 'This removes all recorded payments and sets the invoice back to Unpaid. The customer balance will increase accordingly.'
              : 'This permanently deletes the invoice and its payments. Balances, reports and commissions will update automatically. This cannot be undone.'}
        affectedText={`Affects invoice ${inv.invoice_number}${inv.payments && inv.payments.length ? ` and ${inv.payments.length} payment(s)` : ''}.`}
        onConfirm={runAction}
        busy={busy}
        confirmLabel={action === 'void' ? 'Void Invoice' : action === 'restore' ? 'Restore Invoice' : action === 'reset' ? 'Reset Invoice' : 'Delete Invoice'}
      >
        {(action === 'void' || action === 'delete') && (
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="restore-inventory-checkbox">
            <Checkbox checked={restoreInv} onCheckedChange={(v) => setRestoreInv(!!v)} />
            Restore the items to inventory
          </label>
        )}
      </DangerConfirm>
    </div>
  );
}
